import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warning, X, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Persistent top-of-screen credit banner.
 *
 * Rendering rules (spec Rule 3):
 *   - is_exhausted (100% used)  → RED banner, "processing stopped" copy
 *   - is_near_cap (80-99% used) → AMBER banner, "approaching your limit" copy
 *   - otherwise                 → nothing renders
 *
 * The banner never locks the user out — spec explicitly says continue serving
 * the dashboard, settings, historical data when at 0. We just surface the
 * upgrade prompt and let them keep browsing everything else.
 *
 * Dismissed state is NOT persisted at 100% (must force attention until
 * upgrade or cycle reset). At 80% we allow manual dismiss via session
 * storage so merchants who've seen the warning aren't nagged every page.
 */
export default function CreditsBanner() {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [dismissed80, setDismissed80] = useState(() => {
    return sessionStorage.getItem("credits_banner_80_dismissed") === "1";
  });

  useEffect(() => {
    let cancelled = false;
    const fetchMe = async () => {
      try {
        const res = await api.me();
        if (!cancelled) setCredits(res.data?.user?.credits || null);
      } catch {
        /* auth errors handled elsewhere */
      }
    };
    fetchMe();
    // Refresh every 60s so the banner stays accurate as credits deplete
    // (e.g. during a live Shopify webhook burst)
    const t = setInterval(fetchMe, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!credits) return null;
  if (!credits.is_exhausted && !credits.is_near_cap) return null;
  if (credits.is_near_cap && !credits.is_exhausted && dismissed80) return null;

  const isExhausted = credits.is_exhausted;
  const planTitle = (credits.plan || "").charAt(0).toUpperCase() + (credits.plan || "").slice(1);

  return (
    <div
      className={`sticky top-0 z-40 border-b ${
        isExhausted
          ? "bg-red-50 border-red-300 text-red-900"
          : "bg-amber-50 border-amber-300 text-amber-900"
      }`}
      data-testid={isExhausted ? "credits-exhausted-banner" : "credits-warning-banner"}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center gap-3">
        <Warning size={18} weight="bold" className="flex-shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          {isExhausted ? (
            <>
              <strong>
                You've used all {credits.credits_budget.toLocaleString()} credits in your {planTitle} plan this cycle.
              </strong>
              <span className="ml-1 hidden md:inline">
                New incoming orders are being skipped until you upgrade or your cycle resets.
              </span>
            </>
          ) : (
            <>
              <strong>
                You're at {credits.usage_pct}% of your {credits.credits_budget.toLocaleString()} credits this cycle.
              </strong>
              <span className="ml-1 hidden md:inline">
                {credits.credits_remaining.toLocaleString()} remaining — upgrade now to avoid processing pauses.
              </span>
            </>
          )}
        </div>
        <button
          onClick={() => navigate("/pricing")}
          className={`flex-shrink-0 text-xs uppercase tracking-wider font-bold px-3 py-1.5 inline-flex items-center gap-1 transition-colors ${
            isExhausted
              ? "bg-red-900 text-white hover:bg-red-800"
              : "bg-amber-900 text-white hover:bg-amber-800"
          }`}
          data-testid="credits-banner-upgrade"
        >
          Upgrade <ArrowRight size={12} weight="bold" />
        </button>
        {!isExhausted && (
          <button
            onClick={() => {
              sessionStorage.setItem("credits_banner_80_dismissed", "1");
              setDismissed80(true);
            }}
            className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            title="Dismiss (we'll remind you next time you log in)"
            data-testid="credits-banner-dismiss"
          >
            <X size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}
